/**
 * Store pour la gestion des utilisateurs
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
import Str from '@classes/Str';
export const useUserStore = defineStore('user', (() => {

    // State de la liste des utilisateurs groupés par type (total et users)
    const userGroupList = ref({});

    // State des utilisateurs chargés
    const users = ref([]);

    // State pour les statuts des abonnements
    const userSubscriptionsById = ref({});

    // State de l'utilisateur dont on gère les données
    const currentManagingUser = ref(null);

    // State pour les statuts des périodes d'essai des utilisateurs
    const userTrialPeriodsByUserId = ref({});

    /**
     * Retourne les données du statut de la période d'essai pour l'utilisateur dont on donne l'identifiant en paramètre
     * @param int userId
     * @return object|null
     */
    const getUserTrialPeriodByUserId = computed(() => {
        return ((userId) => {
            const trialPeriodData = (userTrialPeriodsByUserId.value[userId] ?? null);
            return trialPeriodData;
        });
    });

    /**
     * Retourne l'utilisateur dont on gère les données
     * @return object|null
     */
    const getCurrentManagingUser = computed(() => currentManagingUser.value);

    /**
     * Retourne l'abonnement en fonction de l'identifiant de l'utilisateur en paramètres
     * @param int userId
     * @return object|null
     */
    const getUserSubscriptionById = computed(() => {
        return ((userId) => {
            const subscription = (userSubscriptionsById.value[userId] ?? null);
            return subscription;
        });
    });

    /**
     * Retourne les données de liste du type en paramètre (total et users)
     * @param string userType Type d'utilisateur
     * @return object
     */
    const getUserListDataOfType = computed(() => {

        return ((userType) => {
            return (userGroupList.value[userType] || {});
        });
    });

    /**
     * Retourne l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @return object|null
     */
    const getUserById = computed(() => {
        return ((userId) => {
            const index = users.value.findIndex((user) => user.id === userId);
            return (index === -1) ? null : users.value[index];
        });
    });

    /**
     * Met à jour les données de la période d'essai de l'utilisateur en paramètre
     * @param int userId
     * @param object newTrialPeriodData
     * @return void
     */
    const setUserTrialPeriodStatus = (userId, newTrialPeriodData) => {
        userTrialPeriodsByUserId.value[userId] = newTrialPeriodData;
    };

    /**
     * Chargement du statut de la période d'essai de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @return Promise
     */
    const loadUserTrialPeriodStatus = ((userId) => {

        const requestPath = `/api/users/${ userId }/trial-period/status`;

        return axios.get(requestPath).then((response) => {
            const success = (response?.data?.success === true);
            const responseTrialPeriodStatus = (response?.data?.trialPeriodStatus ?? null);

            if(success && responseTrialPeriodStatus !== null) {
                resetUserTrialPeriod(userId);
                setUserTrialPeriodStatus(userId, responseTrialPeriodStatus);
                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Démarrage de la période d'essai de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @return Promise
     */
    const startUserTrialPeriod = ((userId) => {

        const requestPath = `/api/users/${ userId }/trial-period/start`;

        return axios.post(requestPath).then((response) => {
            const success = (response?.data?.success === true);
            const newTrialPeriodStatus = (response?.data?.trialPeriodStatus ?? null);

            if(success && newTrialPeriodStatus !== null) {
                // Utilisation d'un timer pour que les événements des composants se déclenchent
                setTimeout(() => {
                    userTrialPeriodsByUserId.value[userId] = newTrialPeriodStatus;
                }, 200);

                return Promise.resolve();
            }

            return Promise.reject();
        });

    });

    /**
     * Annulation de la période d'essai de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @return Promise
     */
    const cancelUserTrialPeriod = ((userId) => {

        const requestPath = `/api/users/${ userId }/trial-period/cancel`;

        return axios.post(requestPath).then((response) => {
            const success = (response?.data?.success === true);
            const newTrialPeriodStatus = (response?.data?.trialPeriodStatus ?? null);

            if(success && newTrialPeriodStatus !== null) {
                // Utilisation d'un timer pour que les événements des composants se déclenchent
                setTimeout(() => {
                    userTrialPeriodsByUserId.value[userId] = newTrialPeriodStatus;
                }, 200);

                return Promise.resolve();
            }

            return Promise.reject();
        });
    });

    /**
     * Chargement des utilisateurs en fonction du type en paramètre
     * @param userType
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadUserListOfType = ((userType, params) => {

        const userTypeCamel = new Str(userType.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();
        const path = '/api/admin/users/:userType'.replace(':userType', userTypeSlug);

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetUserList(userType);
                }

                updateUserList(userType, response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @param string userTypeCamelCase Type d'utilisateur au format camelCase
     * @param array withParam Données supplémentaire à charger
     * @return Promise
     */
    const loadUserById = ((userId, userTypeCamelCase, withParam = []) => {

        let requiredLoad = false;
        const existingUser = getUserById.value(userId);
        if(existingUser === null) {
            requiredLoad = true;
        } else {
            requiredLoad = (withParam.filter((field) => existingUser[field] === undefined).length > 0);
        }

        if(! requiredLoad) {
            return Promise.resolve(existingUser);
        }

        const userTypeRouteParam = new Str(userTypeCamelCase).toSnakeCase();
        const path = '/api/admin/users/:userType/:userId'
            .replace(':userType', userTypeRouteParam)
            .replace(':userId', userId)
        ;

        return axios.get(path, {
            params: {
                with: withParam.join(',')
            }
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        }).catch(e => {

        });

    });

    /**
     * Ajoute le nombre de crédits demandé pour l'utilisateur en paramètre
     * @param object user
     * @param int creditsToAdd Nombre de crédits à ajouter
     * @return Promise
     */
    const addCredits = ((user, creditsToAdd) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/add-credits'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {
            credits: creditsToAdd
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Modifie le nombre de crédits de l'utilisateur en paramètre
     * @param object user
     * @param int newCredits Nombre de crédits à ajouter
     * @return Promise
     */
    const changeCredits = ((user, newCredits) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/change-credits'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {
            credits: newCredits
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            }
            return Promise.reject();
        });

    });

    /**
     * Modifie le statut de l'agent
     * Si le statut était ENABLED, il devient DISABLED
     * Si le status était DISABLED, il devient ENABLED
     * @param object user
     * @return Promise
     */
    const toggleAgentStatus = ((user) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/toggle-agent-status'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {}).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Mise à jour des données de l'utilisateur dans les données chargées
     * @param object user
     * @return void
     */
    const updateUser = ((user) => {

        const index = users.value.findIndex((current) => current.id === user.id);
        if(index === -1) {
            users.value.push(user);
        } else {
            users.value[index] = { ...users.value[index], ...user };
        }

        // Gestion de la liste
        const currentDataList = getUserListDataOfType.value(user.type);
        if(currentDataList.total === undefined) {
            return;
        }

        // Fusion de l'utilisateur dans la liste où il se trouve
        const newUserList = currentDataList.users;
        const indexFromList = newUserList.findIndex((currentUser) => currentUser.id === user.id);
        if(indexFromList !== -1) {
            const currentUser = userGroupList.value[user.type].users[indexFromList];
            userGroupList.value[user.type].users[indexFromList] = { ...currentUser, ...user };
        }

    });

    /**
     * Supprimer un utilisateur du store
     * @param object user L'utilisateur à supprimer
     * @return void
     */
    const removeUser = ((user) => {

        // Suppression des utilisateurs déjà chargés
        const indexFromLoading = users.value.findIndex((current) => current.id === user.id);
        users.value.splice(indexFromLoading, 1);

        //
        const currentDataList = getUserListDataOfType.value(user.type);
        if(currentDataList.total === undefined) {
            return;
        }

        // Suppression de la liste
        const newUserList = currentDataList.users;
        const indexFromList = newUserList.findIndex((currentUser) => currentUser.id === user.id);
        if(indexFromList !== -1) {
            newUserList.splice(indexFromList, 1);
            userGroupList.value[user.type] = {
                total: currentDataList.total - 1,
                users: newUserList
            };
        }
    });

    /**
     * Mise à jour de la liste des utilisateurs
     * @param string userType
     * @param object newListData Données de la liste (total, items)
     * @return void
     */
    const updateUserList = ((userType, newListData) => {

        const currentData = getUserListDataOfType.value(userType);
        if(currentData.total === undefined) {
            userGroupList.value[userType] = {
                total: newListData.total,
                users: newListData.items
            };

            newListData.items.forEach((user) => {
                updateUser(user);
            });

        } else {
            userGroupList.value[userType].total = newListData.total;

            const currentUsers = userGroupList.value[userType].users;

            const newUsers = newListData.items.filter((item) => ! currentUsers.find((current) => current.id === item.id));
            userGroupList.value[userType].users = [ ...currentUsers, ...newUsers ];

            newUsers.forEach((user) => {
                updateUser(user);
            });
        }

    });

    /**
     * Mise à jour de l'abonnement de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @param object|null newData
     * @return void
     */
    const updateUserSubscriptionById = ((userId, newData) => {
        userSubscriptionsById.value[userId] = newData;
    });

    /**
     * Chargement de l'abonnement de crédit de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @param boolean reload Vrai si on force le chargement des données
     * @return Promise
     */
    const loadCreditSubscriptionById = ((userId, reload = false) => {
        const currentSubscription = (userSubscriptionsById.value[userId] ?? false);
        if(currentSubscription !== false && ! reload) {
            return Promise.resolve();
        }

        const path = `/api/users/${ userId }/credit-subscription`;
        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success) {
                const data = (response.data?.subscription ?? null);
                userSubscriptionsById.value[userId] = data;
                return Promise.resolve(response);
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour du logo de la société de l'utilisateur en paramètre
     * @param Object user
     * @param File newFile
     * @return Promise
     */
    const updateUserSocietyLogo = ((user, newFile) => {

        const path = `/api/users/${ user.id }/account/update-society-logo`;

        const formData = new FormData();
        formData.append('logo', newFile);

        return axios.post(path, formData).then((response) => {
            if (response && response.data && response.data.success && response.data.user) {
                // updateCurrentUserData(response.data.user);
                return Promise.resolve(response.data.user);
            }
            return Promise.reject();
        });
    });

    /**
     * Mise à jour des couleurs de la société de l'utilisateur en paramètre
     * @para Object user
     * @param array newColors
     * @return Promise
     */
    const updateUserSocietyColors = ((user, newColors) => {

        const path = `/api/users/${ user.id }/account/update-society-colors`;

        return axios.post(path, {
            colors: newColors
        }).then((response) => {
            if (response && response.data && response.data.success && response.data.user) {
                return Promise.resolve(response.data.user);
            }
            return Promise.reject();
        });

    });

    /**
     * Upload d'un logo additionnel pour un utilisateur
     * @param Object user
     * @param File newFile
     * @param int|null logoId Si renseigné, le logo à modifier
     * @return Promise
     */
    const uploadUserAdditionalLogo = ((user, newFile, logoId = null) => {

        let path = `/api/users/${ user.id }/account/additional-logos`;
        if(logoId !== null) {
            path += `/${ logoId }`;
        }

        const formData = new FormData();
        formData.append('logo', newFile);

        return axios.post(path, formData).then((response) => {
            if (response && response.data && response.data.success && response.data.society) {

                const userData = Object.assign({}, JSON.parse(JSON.stringify(user)));
                userData.society = response.data.society;

                return Promise.resolve(userData);
            }
            return Promise.reject();
        });
    });

    /**
     * Modifie l'utilisateur dont on gère les données
     * @return object|null
     */
    const setCurrentManagingUser = ((userData) => {
        currentManagingUser.value = userData;
    });

    /**
     * Réinitialisation de la liste des utilisateurs du type en paramètre
     * @param string userType
     * @return void
     */
    const resetUserList = ((userType) => {
        userGroupList.value[userType] = {};
    });

    /**
     * Réinitialisation du statut de la période d'essai d'un utilisateur dans le store
     * @param int userId
     * @return void
     */
    const resetUserTrialPeriod = ((userId) => {
        delete userTrialPeriodsByUserId.value[userId];
    });

    return {
        getUserListDataOfType,
        loadUserListOfType,
        getUserById,
        loadUserById,
        updateUser,
        removeUser,
        addCredits,
        changeCredits,
        toggleAgentStatus,
        /***/
        getUserSubscriptionById,
        updateUserSubscriptionById,
        loadCreditSubscriptionById,
        /***/
        getCurrentManagingUser,
        setCurrentManagingUser,
        updateUserSocietyLogo,
        uploadUserAdditionalLogo,
        updateUserSocietyColors,

        getUserTrialPeriodByUserId,
        loadUserTrialPeriodStatus,
        resetUserTrialPeriod,
        startUserTrialPeriod,
        setUserTrialPeriodStatus,
        cancelUserTrialPeriod
    };

}));
